import type { z } from 'zod';

import type { OfferKind } from '@revelio/core/global-ai-assistant/offer-marker';
import type { ChartValueForm } from '@revelio/core/global-ai-assistant/share-display';

import type { AssistantPostingsMode } from '../../CustomDash/components/DataProvider/postings/postings-metric-mode';
import type { CommandExpiredOutcome } from '../global-ai-assistant-command-store';
import type { ToolEmit } from '../global-ai-tool-progress';
import type { ArtifactCache } from '../global-ai-turn-messages';
import type {
  ChartDataDigest,
  FilterResolutionDigest,
} from '../turn-outcome-digest';

export type ClientToolDeps = {
  /** Catalog option id for the route that is live when the tool executes. */
  getCurrentPageOptionId: () => string | undefined;
  /**
   * Request a NAVIGATION CONFIRMATION instead of navigating. The hard rule is
   * that the assistant never navigates without an explicit user click, so the
   * `navigate` tool is terminal: it appends a confirmation chip and ends the
   * turn. Resolves the optionId to a destination page name, or reports it is
   * not a known page so the model doesn't claim a phantom navigation.
   */
  requestNavigateConfirm: (input: {
    optionId: string;
    /**
     * Whether the nav-confirm chip resumes the turn (replaying the user's
     * prompt) after landing. Default true — a plain `navigate` may still have a
     * filter/task to finish on the destination. `offer_page_or_custom` sets it
     * false: its page chip is a terminal "just land me there" action, so
     * replaying the (often causal) prompt would make the resumed turn answer it
     * by filtering the page — an unwanted loop.
     */
    withContinuation?: boolean;
    /**
     * Resolved search_filters artifact for a DATA-GROUNDED offer: the click
     * applies this exact artifact on the destination (deterministic text=link
     * — the page shows the same slice the narrated figures came from). Passed
     * by `offer_page_or_custom` when the model supplies `filterArtifactId`;
     * absent → the click stays pure navigation (existing behavior).
     */
    resolvedFilters?: unknown;
    /** Entity anchor for `resolvedFilters`. */
    entityType?: string;
  }) => { pageName: string; messageId: string } | { unknownPage: true };
  applyFilters: (input: {
    filterResponse: unknown;
    target: string;
    merge?: boolean;
  }) => Promise<FilterResolutionDigest>;
  renderChart: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
    valueForm?: ChartValueForm;
    standardPagePath?: string;
    postingsMode?: AssistantPostingsMode;
  }) => Promise<ChartDataDigest>;
  /**
   * Headless single-value digest fetch (DP3) — offscreen digest host, no
   * chart UI, no transcript message. See ClientToolHandles.fetchChartDigest.
   */
  fetchChartDigest: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
    valueForm?: ChartValueForm;
    postingsMode?: AssistantPostingsMode;
  }) => Promise<ChartDataDigest>;
  /** Append the card-less offer:page context message for a successful answer. */
  appendAnswerContextChart: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    standardPagePath: string;
  }) => void;
  requestCreateCustomPage: (
    input: {
      prompt: string;
      entityType: string;
      /**
       * Force OFFER mode: always append the build-confirm chip, never BUILD-HERE,
       * regardless of the pending-build ref (and clear that ref if stale). Used
       * by `offer_page_or_custom`, which is a pure OFFER and must never build —
       * the build-ref belongs to the `create_custom_page` build-chip resume flow.
       */
      forceOffer?: boolean;
      /**
       * Resolved search_filters artifact for a DATA-GROUNDED offer (the same
       * artifact the paired nav chip carries). Stored on the build chip as the
       * landing-filter fallback for the lazy build.
       */
      groundedFilters?: unknown;
    },
    emit?: ToolEmit
  ) => Promise<
    | { offered: true; messageId: string }
    | {
        built: true;
        ok: boolean;
        digests: ChartDataDigest[];
        expectedCount?: number;
        /** See `updateCurrentPage`'s `failureReason`. */
        failureReason?: CommandExpiredOutcome;
      }
  >;
  updateCurrentPage: (
    input: { prompt: string },
    emit?: ToolEmit
  ) => Promise<{
    ok: boolean;
    digests: ChartDataDigest[];
    expectedCount?: number;
    /**
     * `'expired'` means no page bridge ever claimed the command, so the work
     * never started — narrated differently from a claimed-but-unconfirmed
     * command, which may still be finishing.
     */
    failureReason?: CommandExpiredOutcome;
  }>;
  /**
   * Offer one or more reports to the user as clickable chips (it does NOT
   * launch the reports). Returns the resolved display names (order-preserving,
   * skipping any reportType that can't be resolved) so the model can name them
   * in its narration, plus the appended `report_offers` message id — the
   * `offer:report` stamp source for the narration's inline trigger(s).
   */
  offerReport: (input: {
    reportTypes: string[];
    prompt: string;
  }) => Promise<{ reportNames: string[]; messageId?: string }>;
  askUser: (input: { question: string; options?: string[] }) => void;
  /**
   * Append the offer tool's `narration` argument as the turn's assistant `text`
   * message. The drawer renders it through `parseOfferSegments`, so its
   * `[phrase](offer:nav|build)` markers become inline trigger spans and
   * `turnNarrationHasOffer` suppresses the now-redundant chip. The offer
   * handlers use it so user-facing offer text lives in a validated tool param
   * rather than freeform prose.
   */
  appendAssistantText: (
    text: string,
    offerTargets?: Partial<Record<OfferKind, string>>
  ) => void;
  /** This turn's latest single-value answer chart: the offer:page stamp source. */
  getLastStandardPageAnswerChart: () => StandardPageAnswerChart | undefined;
};

/** A single-value answer chart and the catalog id of its standard page. */
export type StandardPageAnswerChart = { id: string; pageOptionId?: string };

export type ClientToolResult = { resultForModel: string; endsTurn?: boolean };

/**
 * There is no structured status field — the `ERROR` prefix IS the failure
 * contract, so a failure phrased as prose ("Couldn't load…") reads as success
 * to every consumer, the runner's report ride-along gate included.
 */
export const isClientToolFailure = (result: ClientToolResult): boolean =>
  result.resultForModel.startsWith('ERROR');

/**
 * A turn-aborted no-op (`turnAbortedNoOpResult`). Distinct from a failure on
 * the same prefix contract: nothing broke, so it is not an `ERROR` — but the
 * user never saw the chart/offer either, so it must not count as output. Both
 * the outcome funnel and the report ride-along have to exclude it explicitly,
 * because it RESOLVES where a genuine miss would have thrown.
 */
export const isTurnCancelledResult = (result: ClientToolResult): boolean =>
  result.resultForModel.startsWith('CANCELLED');

/** Everything a `run` may touch beyond its own validated input. */
type ClientToolContext = {
  artifacts: ArtifactCache;
  deps: ClientToolDeps;
  /** Only create_custom_page emits; the rest ignore it. */
  emit?: ToolEmit;
  /**
   * The turn's abort signal. The digest layer RESOLVES on abort (synthetic
   * digest) rather than rejecting, so an awaiting tool body keeps executing
   * after the user stopped the turn — and its post-await side effects
   * (transcript appends, offer wiring) land on a turn that is already dead.
   * User stops are rescued by the turn-stop rewind, but engine-unmount /
   * new-turn aborts have no rewind, so the stray write survives. Tools that
   * await MUST re-check this after every await and return
   * `turnAbortedNoOpResult` before any side effect.
   */
  signal?: AbortSignal;
};

/**
 * What the turn loop does with the round's STREAMED narration for this tool,
 * read at its two narration seams and nowhere else. Inert for non-terminal
 * tools, which never reach the answer seam.
 *
 * - `'answer'` (the default): on a terminal result, surface the streamed text
 *   as the round's answer.
 * - `'drop'`: never surface it. Two reasons share the behaviour — the offer
 *   tools append their own validated `narration`, and `ask_user`'s preamble
 *   describes work the model is NOT doing.
 * - `'flush-before-chip'`: surface it BEFORE the tool runs, so it lands above
 *   what the tool appends (offer_report's report chips).
 */
export type ClientToolNarrationPolicy = 'answer' | 'drop' | 'flush-before-chip';

/**
 * A schema for the tool's MODEL-AUTHORED input plus its handler. The registry
 * parses before dispatch, so a `run` never sees an unvalidated shape. The
 * `z.ZodTypeAny` default is what lets the registry table hold eleven different
 * schemas without a cast per entry — as the server registry does
 * (apps/dash-meta/.../turn-tool-registry.ts `buildSchemas`).
 */
export type ClientToolDefinition<Schema extends z.ZodTypeAny = z.ZodTypeAny> = {
  schema: Schema;
  /** Omitted means `'answer'`. */
  narrationPolicy?: ClientToolNarrationPolicy;
  run(
    input: z.output<Schema>,
    ctx: ClientToolContext
  ): Promise<ClientToolResult> | ClientToolResult;
};
